import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { convertThenFormat } from "../common-data/Converts";
import { fetchGymwiseODMData } from "../../redux/reducers/apiSlice";
import CmnFilters from "../lead-management/common/CmnFilters";
import CommonTable from "../common-components/common-tables/CommonTable";
import { commonPostPutApi } from "../../redux/reducers/commonApiSlice";
import { gym_odm_lead_capture_toggle } from "../../data/ApiEndpoints";
import { InfoCircleIcon } from "../../images/icons/icons";
import { isDev } from "../common-data/Commons";
import { IS_CRUD_ACCESS_BY_EMAIL } from "../common-data/Accessibles";

const GymwiseODM = ({ userInfoData }) => {
  const dispatch = useDispatch();
  const { gymWiseODMLoading, gymWiseODMError, gymWiseODM } = useSelector(
    (state) => state.api
  );
  const { postPutLoading } = useSelector((state) => state.common);
  const infoIconRef = useRef(null);

  const isCRUDAllowed = isDev || IS_CRUD_ACCESS_BY_EMAIL(userInfoData?.email);

  const [showInfoPopover, setShowInfoPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  const iniAllFilter = {
    leadAddDateFrom: "",
    leadAddDateTo: "",
  };
  const [allFilters, setAllFilters] = useState(iniAllFilter);
  const iniFilters = {
    start_date: allFilters.leadAddDateFrom || "",
    end_date: allFilters.leadAddDateTo || "",
  };
  const [filters, setFilters] = useState(iniFilters);
  const [gymWiseStatsData, setGymWiseStatsData] = useState([]);

  const [selectedRows, setSelectedRows] = useState([]);

  const onResetFilters = () => {
    setAllFilters(iniAllFilter);
  };

  const handleInfoIconClick = (e) => {
    e.stopPropagation();
    if (infoIconRef.current) {
      const rect = infoIconRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + 8,
        left: rect.left - 130,
      });
    }
    setShowInfoPopover(!showInfoPopover);
  };

  function getGymwiseODM(filters) {
    dispatch(
      fetchGymwiseODMData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  }

  const handleCheckboxChange = async (event, row) => {
    const isChecked = event.target.checked;

    const previousData = [...gymWiseStatsData];

    const updatedRows = gymWiseStatsData.map((r) =>
      r.gym_id === row.gym_id
        ? { ...r, odm_lead_capture: isChecked ? 1 : 0 }
        : r
    );

    setSelectedRows(
      isChecked
        ? [...selectedRows, { ...row, odm_lead_capture: isChecked ? 1 : 0 }]
        : selectedRows.filter(
            (selectedRow) => selectedRow.gym_id !== row.gym_id
          )
    );

    setGymWiseStatsData(updatedRows);

    const payload = {
      gym_id: row?.gym_id || "",
      odm_lead_capture: isChecked ? 1 : 0,
    };

    try {
      const result = await dispatch(
        commonPostPutApi({
          method: "POST",
          endpoint: gym_odm_lead_capture_toggle,
          payload,
          token: userInfoData?.token || "",
        })
      );

      if (result?.payload?.status) {
        toast.success(result?.payload?.message || "Updated successfully!");
      } else {
        const errorMessage = result?.payload?.message || result?.error?.message;
        toast.error(errorMessage || "Something went wrong!");
        setGymWiseStatsData(previousData);
      }
    } catch (error) {
      console.error("Error toggling ODM lead capture:", error);
      toast.error("Something went wrong!");
      setGymWiseStatsData(previousData);
    }
  };

  useEffect(() => {
    getGymwiseODM(filters);
  }, [filters]);

  useEffect(() => {
    if (
      !gymWiseODMLoading &&
      !gymWiseODMError &&
      gymWiseODM?.status &&
      gymWiseODM?.data?.length > 0
    ) {
      setGymWiseStatsData(gymWiseODM.data);
    } else {
      setGymWiseStatsData([]);
    }
  }, [gymWiseODMLoading, gymWiseODMError, gymWiseODM]);

  useEffect(() => {
    setFilters((prevs) => ({
      ...prevs,
      start_date: allFilters.leadAddDateFrom || "",
      end_date: allFilters.leadAddDateTo || "",
    }));
  }, [allFilters.leadAddDateFrom, allFilters.leadAddDateTo]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (infoIconRef.current && !infoIconRef.current.contains(event.target)) {
        setShowInfoPopover(false);
      }
    };

    if (showInfoPopover) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showInfoPopover]);

  const tbColumns = [
    {
      dataField: "gym_name",
      text: (
        <div className="tb-header-with-info">
          <span>Gym Name</span>
          {isCRUDAllowed && (
            <span
              className="tb-info-icon"
              ref={infoIconRef}
              onClick={handleInfoIconClick}
            >
              <InfoCircleIcon />
            </span>
          )}
          {showInfoPopover && (
            <div
              className="tb-info-popover"
              style={{
                top: popoverPosition.top,
                left: popoverPosition.left,
              }}
            >
              <div className="tb-info-popover-content">
                <p>
                  <strong>Enable/Disable ODM:</strong>
                </p>
                <p>
                  When <strong>checked</strong>, ODM (One Day Membership) will
                  be <strong>enabled</strong> for the gym, allowing users to
                  purchase ODM memberships.
                </p>
                <p>
                  When <strong>unchecked</strong>, ODM will be{" "}
                  <strong>disabled</strong> for the gym, and users won't be able
                  to buy ODM memberships.
                </p>
              </div>
            </div>
          )}
        </div>
      ),
      formatter: (_, row) => {
        const isTotal = row?.gym_name?.toLowerCase() === "total";

        return (
          <div className="tb-flex">
            {isCRUDAllowed && !isTotal ? (
              <input
                type="checkbox"
                className="pp-checkbox"
                checked={row?.odm_lead_capture === 1}
                onChange={(event) => handleCheckboxChange(event, row)}
                disabled={postPutLoading || gymWiseODMLoading}
                aria-label={`Select ${row.gym_name}`}
              />
            ) : null}

            <span className={`tb-data ${isTotal ? "tb-badge-success" : ""}`}>
              {row?.gym_name}
            </span>
          </div>
        );
      },
    },
    {
      dataField: "gym_name",
      text: "total members",
      centerAligned: true,
      formatter: (_, row) => {
        const isTotal = row?.gym_name?.toLowerCase() === "total";

        return (
          <span className={`tb-data ${isTotal ? "tb-badge-success" : ""}`}>
            {convertThenFormat(row?.total || 0)}
          </span>
        );
      },
    },
    {
      dataField: "total_completed",
      text: "ODM Completed",
      centerAligned: true,
      formatter: (_, row) => {
        const isTotal = row?.gym_name?.toLowerCase() === "total";

        return (
          <span className={`tb-data ${isTotal ? "tb-badge-success" : ""}`}>
            {convertThenFormat(row?.total_completed || 0)}
          </span>
        );
      },
    },
    {
      dataField: "total_pending",
      text: "ODM Pending",
      centerAligned: true,
      formatter: (_, row) => {
        const isTotal = row?.gym_name?.toLowerCase() === "total";

        return (
          <span className={`tb-data ${isTotal ? "tb-badge-success" : ""}`}>
            {convertThenFormat(row?.total_pending || 0)}
          </span>
        );
      },
    },
    {
      dataField: "total_converted",
      text: "ODM Converted",
      centerAligned: true,
      formatter: (_, row) => {
        const isTotal = row?.gym_name?.toLowerCase() === "total";

        return (
          <span className={`tb-data ${isTotal ? "tb-badge-success" : ""}`}>
            {convertThenFormat(row?.total_converted || 0)}
          </span>
        );
      },
    },
    {
      dataField: "total_converted_amount",
      text: "ODM Converted Amount",
      centerAligned: true,
      formatter: (_, row) => {
        const isTotal = row?.gym_name?.toLowerCase() === "total";

        return (
          <span className={`tb-data ${isTotal ? "tb-badge-success" : ""}`}>
            ₹{convertThenFormat(row?.total_converted_amount || 0)}
          </span>
        );
      },
    },
    {
      dataField: "total_not_converted",
      text: "ODM Not Converted",
      centerAligned: true,
      formatter: (_, row) => {
        const isTotal = row?.gym_name?.toLowerCase() === "total";

        return (
          <span className={`tb-data ${isTotal ? "tb-badge-success" : ""}`}>
            {convertThenFormat(row?.total_not_converted || 0)}
          </span>
        );
      },
    },
    {
      dataField: "total_added_to_lead",
      text: "added to LMS",
      centerAligned: true,
      formatter: (_, row) => {
        const isTotal = row?.gym_name?.toLowerCase() === "total";

        return (
          <span className={`tb-data ${isTotal ? "tb-badge-success" : ""}`}>
            {convertThenFormat(row?.total_added_to_lead || 0)}
          </span>
        );
      },
    },
    {
      dataField: "total_not_added_to_lead",
      text: "Not Added To LMS",
      centerAligned: true,
      formatter: (_, row) => {
        const isTotal = row?.gym_name?.toLowerCase() === "total";

        return (
          <span className={`tb-data ${isTotal ? "tb-badge-success" : ""}`}>
            {convertThenFormat(row?.total_not_added_to_lead || 0)}
          </span>
        );
      },
    },
  ];

  return (
    <>
      <CmnFilters
        from="odm-gymwise"
        userInfoData={userInfoData}
        onClickReset={onResetFilters}
        filters={allFilters}
        setFilters={setAllFilters}
        isReload={() => {
          getGymwiseODM(filters);
        }}
        isLoading={gymWiseODMLoading || postPutLoading}
      />

      <div className="common-table-container tb-bordered mt15">
        <CommonTable
          tableData={gymWiseStatsData}
          tableColumns={tbColumns}
          rowClassName={(row) => (row?.checked ? "tb-row-selected" : "")}
          isLoading={gymWiseODMLoading}
        />
      </div>
    </>
  );
};

export default GymwiseODM;
